import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Camera, Check } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, imgUrl } from "@/lib/api";
import { Stars } from "@/components/Stars";
import { REVIEW } from "@/constants/testIds";

const input = "w-full px-4 py-2.5 rounded-2xl border border-[#E7E0D3] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#E06D44]/40";

const ReviewForm = ({ item, orderId, token, onDone }) => {
  const [rating, setRating] = useState(0);
  const [text, setText] = useState("");
  const [name, setName] = useState("");
  const [pet, setPet] = useState("");
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!rating) return toast.error("Please pick 1 to 5 stars");
    setBusy(true);
    const form = new FormData();
    Object.entries({ order_id: orderId, t: token, slug: item.slug, rating, text, name, pet_name: pet }).forEach(([k, v]) => form.append(k, v));
    if (photo) form.append("photo", photo);
    try {
      await api.post("/reviews", form);
      toast.success("Thank you! Kiddee is wagging.");
      onDone(item.slug);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="mt-4 space-y-3">
      <Stars value={rating} size={26} onPick={setRating} testId={(n) => REVIEW.star(item.slug, n)} />
      <textarea data-testid={REVIEW.text(item.slug)} value={text} onChange={(e) => setText(e.target.value)} maxLength={1000} rows={3} placeholder="What does your pet think of it?" className={input} />
      <div className="grid sm:grid-cols-2 gap-3">
        <input data-testid={REVIEW.name(item.slug)} value={name} onChange={(e) => setName(e.target.value)} maxLength={40} placeholder="Your first name (optional)" className={input} />
        <input data-testid={REVIEW.petName(item.slug)} value={pet} onChange={(e) => setPet(e.target.value)} maxLength={40} placeholder="Your pet's name (optional)" className={input} />
      </div>
      <label className="flex items-center gap-2 text-sm text-stone-600 cursor-pointer w-fit">
        <span className="w-10 h-10 rounded-full bg-[#F0EBE1] grid place-items-center"><Camera size={17} /></span>
        {photo ? photo.name : "Add a photo of your pet (optional)"}
        <input data-testid={REVIEW.photo(item.slug)} type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif" className="sr-only" onChange={(e) => setPhoto(e.target.files?.[0] || null)} />
      </label>
      <button data-testid={REVIEW.submit(item.slug)} disabled={busy} className="px-6 py-3 rounded-full bg-[#E06D44] text-white font-medium hover:bg-[#C95B33] disabled:opacity-50 transition-colors">
        {busy ? "Sending…" : "Send review"}
      </button>
    </form>
  );
};

export default function ReviewPage() {
  const { orderId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("t") || "";
  const [data, setData] = useState(undefined);
  useEffect(() => {
    api.get(`/reviews/order/${orderId}`, { params: { t: token } }).then(({ data }) => setData(data)).catch(() => setData(null));
  }, [orderId, token]);
  const done = (slug) => setData((d) => ({ ...d, items: d.items.map((i) => (i.slug === slug ? { ...i, reviewed: true } : i)) }));

  if (data === null)
    return (
      <main data-testid={REVIEW.invalid} className="pt-32 pb-16 max-w-xl mx-auto px-6 text-center">
        <h1 className="font-heading text-3xl font-bold">This review link isn't valid</h1>
        <p className="mt-3 text-stone-600">Please use the button in your review email.</p>
        <Link to="/shop" className="mt-6 inline-block underline">Back to the shop</Link>
      </main>
    );
  if (!data) return <main className="pt-32 min-h-[60vh]" />;
  return (
    <main data-testid={REVIEW.page} className="pt-28 pb-16 max-w-2xl mx-auto px-6 fade-up">
      <div className="flex items-center gap-4">
        <img src="/media/kiddee/2.jpg" alt="Kiddee" className="w-16 h-16 rounded-full object-cover" />
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">How did it go?</h1>
          <p className="text-stone-600">Kiddee wants the honest scoop. Reviews appear on the shop after a quick check.</p>
        </div>
      </div>
      <div className="mt-8 space-y-4">
        {data.items.map((item) => (
          <section key={item.slug} data-testid={REVIEW.item(item.slug)} className="bg-white border border-[#F0EBE1] rounded-[1.5rem] p-5">
            <div className="flex items-center gap-3">
              {item.image && <img src={imgUrl(item.image)} alt="" className="w-14 h-14 rounded-xl object-cover" />}
              <p className="font-heading font-bold">{item.name}</p>
            </div>
            {item.reviewed
              ? <p data-testid={REVIEW.done(item.slug)} className="mt-4 flex items-center gap-2 text-sm text-[#4E7B68]"><Check size={16} /> Thanks, we've got your review.</p>
              : <ReviewForm item={item} orderId={orderId} token={token} onDone={done} />}
          </section>
        ))}
      </div>
    </main>
  );
}
